import React, { useRef, useState } from "react";
import { api, formatApiErrorDetail, fileToBase64 } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Upload, CheckCircle2, X, AlertTriangle, Lock } from "lucide-react";

export default function StepDelivery({ data, reload }) {
  const { shipment, delivery } = data;
  // Allow editing during delivery AND closure (so admin can amend even when claim is open)
  const isEditable = ["delivery", "closure"].includes(shipment.current_stage);
  const isClaimOpen = shipment.status === "claim";
  const isClosed = shipment.status === "completed" && shipment.current_stage === "closure";
  const saved = delivery;
  const [form, setForm] = useState({
    delivery_status: saved?.delivery_status || "delivered",
    condition: saved?.condition || "good",
    delivered_on: saved?.delivered_on || new Date().toISOString().slice(0, 10),
    temperature_logs: saved?.temperature_logs || "",
    photos: saved?.photos || [],
    survey_report_filename: saved?.survey_report_filename || "",
    survey_report_base64: saved?.survey_report_base64 || "",
    notes: saved?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const photoRef = useRef(null);
  const cameraRef = useRef(null);
  const reportRef = useRef(null);

  const addPhoto = async (file) => {
    if (!file) return;
    const b64 = await fileToBase64(file);
    setForm((f) => ({ ...f, photos: [...f.photos, b64] }));
  };
  const removePhoto = (idx) => setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));
  const setReport = async (file) => {
    if (!file) return;
    const b64 = await fileToBase64(file);
    setForm((f) => ({ ...f, survey_report_filename: file.name, survey_report_base64: b64 }));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data: resp } = await api.post(`/shipments/${shipment.id}/delivery`, form);
      if (resp.final_status === "claim") {
        toast.warning("Cargo damaged — claim module opened");
      } else {
        toast.success("Delivery confirmed in GOOD condition");
      }
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const closeNow = async () => {
    if (!window.confirm("Close this shipment now? It will be marked as completed.")) return;
    setBusy(true);
    try {
      await api.post(`/shipments/${shipment.id}/close`, {});
      toast.success("Shipment closed");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-widest text-stone-400">Step 6</div>
      <h2 className="font-display text-xl font-bold tracking-tight text-[#1F1F1F] mt-1">Delivery & Quality</h2>
      <p className="text-sm text-stone-500 mb-4">Record delivery status, cargo condition, and evidence.</p>

      {isClaimOpen && (
        <div className="mb-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-xs text-amber-800 flex items-center gap-2" data-testid="delivery-claim-banner">
          <AlertTriangle className="w-4 h-4" /> A claim is open. You can still amend delivery and close from here once resolved.
        </div>
      )}
      {isClosed && (
        <div className="mb-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-xs text-emerald-800 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> Shipment closed.
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Delivery Status">
          <select disabled={!isEditable} value={form.delivery_status} onChange={(e) => setForm({ ...form, delivery_status: e.target.value })} className="input" data-testid="delivery-status">
            <option value="delivered">Delivered</option>
            <option value="delayed">Delayed</option>
          </select>
        </Field>
        <Field label="Condition">
          <select disabled={!isEditable} value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="input" data-testid="delivery-condition">
            <option value="good">Good</option>
            <option value="damaged">Damaged</option>
          </select>
        </Field>
        <Field label="Delivered On">
          <input type="date" disabled={!isEditable} value={form.delivered_on} onChange={(e) => setForm({ ...form, delivered_on: e.target.value })} className="input" data-testid="delivery-date" />
        </Field>
        <Field label="Temperature Logs">
          <input disabled={!isEditable} placeholder="e.g. 4-6°C maintained" value={form.temperature_logs} onChange={(e) => setForm({ ...form, temperature_logs: e.target.value })} className="input" data-testid="delivery-temp" />
        </Field>
      </div>

      <div className="mt-4">
        <div className="text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-2">Cargo Photos</div>
        <div className="grid grid-cols-3 gap-2">
          {form.photos.map((p, i) => (
            <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-stone-200">
              <img src={p} alt="cargo" className="w-full h-full object-cover" />
              {isEditable && (
                <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1" data-testid={`remove-photo-${i}`}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
          {isEditable && (
            <>
              <button onClick={() => cameraRef.current?.click()} className="aspect-square rounded-xl border-2 border-dashed border-stone-300 flex flex-col items-center justify-center text-xs text-stone-500 hover:border-[#A8323F] hover:text-[#6B0F1A]" data-testid="add-photo-camera">
                <Camera className="w-5 h-5 mb-1" /> Camera
              </button>
              <button onClick={() => photoRef.current?.click()} className="aspect-square rounded-xl border-2 border-dashed border-stone-300 flex flex-col items-center justify-center text-xs text-stone-500 hover:border-[#A8323F] hover:text-[#6B0F1A]" data-testid="add-photo-file">
                <Upload className="w-5 h-5 mb-1" /> Upload
              </button>
            </>
          )}
        </div>
      </div>

      <div className="mt-4">
        <Field label="Survey Report">
          {form.survey_report_filename ? (
            <div className="flex items-center justify-between bg-[#F4E8E9] border border-[#A8323F] rounded-lg px-3 py-2">
              <span className="text-sm text-[#6B0F1A] truncate">{form.survey_report_filename}</span>
              {isEditable && (
                <button onClick={() => setForm((f) => ({ ...f, survey_report_filename: "", survey_report_base64: "" }))} className="text-xs text-red-600 font-semibold" data-testid="remove-report">Remove</button>
              )}
            </div>
          ) : isEditable ? (
            <button onClick={() => reportRef.current?.click()} className="w-full rounded-lg border-2 border-dashed border-stone-300 py-3 text-xs text-stone-500 hover:border-[#A8323F]" data-testid="upload-report">
              <Upload className="w-4 h-4 inline mr-1" /> Upload survey report
            </button>
          ) : <div className="text-sm text-stone-400">—</div>}
        </Field>
      </div>

      <div className="mt-3">
        <Field label="Notes">
          <textarea disabled={!isEditable} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" data-testid="delivery-notes" />
        </Field>
      </div>

      {form.condition === "damaged" && (
        <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
          <div className="text-xs text-amber-800">
            Cargo marked as damaged. Submitting will automatically open a claim for review.
          </div>
        </div>
      )}

      {isEditable && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={submit} disabled={busy} className="btn-primary" data-testid="delivery-submit">
            <CheckCircle2 className="w-4 h-4" /> {saved ? "Save delivery" : "Confirm delivery"}
          </button>
          {!isClosed && (
            <button onClick={closeNow} disabled={busy} className="inline-flex items-center gap-1.5 bg-white border-2 border-[#6B0F1A] text-[#6B0F1A] hover:bg-[#F4E8E9] font-semibold rounded-xl px-4 py-2 text-sm disabled:opacity-50" data-testid="delivery-close-shipment">
              <Lock className="w-4 h-4" /> Close shipment
            </button>
          )}
        </div>
      )}

      <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => addPhoto(e.target.files?.[0])} />
      <input ref={photoRef} type="file" accept="image/*" hidden onChange={(e) => addPhoto(e.target.files?.[0])} />
      <input ref={reportRef} type="file" hidden onChange={(e) => setReport(e.target.files?.[0])} />

      <style>{`
        .input { width:100%; background:#FBFAF9; border:1px solid #E5E0DC; border-radius:.5rem; padding:.6rem .9rem; color:#1F1F1F; outline:none; font-size: 0.875rem; }
        .input:focus { background:white; border-color:#A8323F; box-shadow:0 0 0 3px rgba(168,50,63,0.15);}
        .input:disabled { background:#F2EBE8; color:#666666; }
        .btn-primary { display:inline-flex; align-items:center; gap:.4rem; background:#6B0F1A; color:white; font-weight:600; border-radius:.75rem; padding:.625rem 1rem; font-size:.875rem; }
        .btn-primary:hover:not(:disabled) { background:#56121C; }
        .btn-primary:disabled { opacity:.6; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-widest text-stone-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
